/*
  Rugs2Go theme additions (loaded after empire.js)
  1. Cart drawer: opens after Add to cart (see the small hook in empire.js, AddToCartFlyout._updateCart)
     and from the header cart icon. Content is re-rendered by Shopify through the Section Rendering API.
  2. Shop Pay installments: Shopify's <shopify-payment-terms> element renders "4 interest-free installments, or from
     $X/mo" once monthly plans qualify. Rugs2Go wants the plain Pay in 4 line everywhere, so the line is rebuilt from
     the element's own data ("Pay in 4 interest-free installments of $X with shop") and the native text is hidden.
     The native element stays in the DOM so its Learn more modal still opens from the (?) button. If the data is not
     there (Shop Pay Installments off, ineligible price) nothing custom is shown. On product pages the hidden
     installment form is kept in sync with the selected variant.
  3. Cart page: refreshes the Shop Pay installments line + free-shipping bar after quantity changes.
*/
(function () {
  'use strict';

  var SECTION_ID = 'cart-drawer';
  var doc = document;

  function $(sel, ctx) { return (ctx || doc).querySelector(sel); }
  function $$(sel, ctx) { return Array.prototype.slice.call((ctx || doc).querySelectorAll(sel)); }

  function notifyHeader(cart) {
    // Empire's header listens for this jQuery event to update the cart count bubble
    if (window.jQuery && cart) {
      window.jQuery('.site-header-wrapper').trigger('cartcount:update', { response: cart });
    }
  }

  function rootPath() {
    var root = window.Shopify && window.Shopify.routes && window.Shopify.routes.root;
    return root || '/';
  }

  function fetchSection(sectionId, path) {
    var base = path ? rootPath().replace(/\/$/, '') + path : rootPath();
    var url = base + (base.indexOf('?') > -1 ? '&' : '?') + 'section_id=' + sectionId;
    return fetch(url, { credentials: 'same-origin', headers: { 'Accept': 'text/html' } })
      .then(function (r) { if (!r.ok) { throw new Error('Section render failed'); } return r.text(); })
      .then(function (html) { return new DOMParser().parseFromString(html, 'text/html'); });
  }

  function cartRequest(endpoint, body) {
    return fetch(endpoint, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify(body)
    }).then(function (r) {
      return r.json().then(function (json) {
        if (!r.ok) { throw new Error(json.description || json.message || 'Cart update failed'); }
        return json;
      });
    });
  }

  /* ------------------------------------------------------------------ */
  /* Cart drawer                                                         */
  /* ------------------------------------------------------------------ */
  var Drawer = {
    el: null,
    overlay: null,
    isOpen: false,
    busy: false,
    lastFocus: null,

    init: function () {
      this.el = $('[data-r2g-drawer]');
      this.overlay = $('[data-r2g-drawer-overlay]');
      if (!this.el || !this.overlay) { return false; }

      var self = this;
      this.overlay.addEventListener('click', function () { self.close(); });
      doc.addEventListener('keydown', function (e) {
        if (e.key === 'Escape' && self.isOpen) { self.close(); }
      });

      // Delegated events inside the drawer (content is replaced on every refresh)
      this.el.addEventListener('click', function (e) {
        var t = e.target;
        if (t.closest('[data-r2g-drawer-close]')) {
          if (!t.closest('a')) { e.preventDefault(); }
          self.close();
          return;
        }
        var plus = t.closest('[data-r2g-qty-plus]');
        var minus = t.closest('[data-r2g-qty-minus]');
        var remove = t.closest('[data-r2g-remove]');
        if (plus || minus || remove) {
          e.preventDefault();
          var item = t.closest('[data-r2g-line]');
          if (!item) { return; }
          var input = $('[data-r2g-qty-input]', item);
          var qty = parseInt(input && input.value, 10) || 0;
          if (plus) { qty += 1; }
          if (minus) { qty = Math.max(0, qty - 1); }
          if (remove) { qty = 0; }
          self.changeLine(parseInt(item.getAttribute('data-r2g-line'), 10), qty);
        }
      });
      this.el.addEventListener('change', function (e) {
        var input = e.target.closest('[data-r2g-qty-input]');
        if (!input) { return; }
        var item = input.closest('[data-r2g-line]');
        var qty = Math.max(0, parseInt(input.value, 10) || 0);
        self.changeLine(parseInt(item.getAttribute('data-r2g-line'), 10), qty);
      });

      // Header cart icon opens the drawer (except on the cart page itself)
      if (!$('[data-section-type="static-cart"]')) {
        $$('.site-header-cart--button').forEach(function (btn) {
          btn.addEventListener('click', function (e) {
            e.preventDefault();
            self.open();
          });
        });
      }

      // Hook fired by empire.js after a successful /cart/add.js
      doc.addEventListener('r2g:cart:added', function (e) {
        self.open(e.detail && e.detail.cart);
      });

      return true;
    },

    open: function (cart) {
      var self = this;
      this.lastFocus = doc.activeElement;
      this.el.classList.add('is-loading');
      this.refresh(cart).then(function () {
        self.show();
      }, function () {
        // If the section render fails for any reason, fall back to the cart page
        window.location.href = rootPath().replace(/\/$/, '') + '/cart';
      });
    },

    show: function () {
      var self = this;
      this.el.setAttribute('aria-hidden', 'false');
      this.overlay.classList.add('is-visible');
      doc.documentElement.classList.add('r2g-drawer-open');
      // next frame so the transition runs
      requestAnimationFrame(function () {
        self.el.classList.add('is-open');
        self.isOpen = true;
        var closeBtn = $('[data-r2g-drawer-close]', self.el);
        if (closeBtn) { closeBtn.focus(); }
      });
    },

    close: function () {
      if (!this.isOpen) { return; }
      this.isOpen = false;
      this.el.classList.remove('is-open');
      this.overlay.classList.remove('is-visible');
      doc.documentElement.classList.remove('r2g-drawer-open');
      this.el.setAttribute('aria-hidden', 'true');
      if (this.lastFocus && typeof this.lastFocus.focus === 'function') { this.lastFocus.focus(); }
    },

    refresh: function (cart) {
      var self = this;
      return fetchSection(SECTION_ID).then(function (dom) {
        var fresh = $('[data-r2g-drawer-content]', dom);
        var current = $('[data-r2g-drawer-content]', self.el);
        if (fresh && current) {
          current.innerHTML = fresh.innerHTML;
          Installments.renderAll(current);
        }
        self.el.classList.remove('is-loading');
        if (cart) { notifyHeader(cart); }
      });
    },

    changeLine: function (line, quantity) {
      var self = this;
      if (this.busy || !line) { return; }
      this.busy = true;
      this.el.classList.add('is-loading');
      cartRequest(rootPath().replace(/\/$/, '') + '/cart/change.js', { line: line, quantity: quantity })
        .then(function (cart) {
          notifyHeader(cart);
          return self.refresh();
        })
        .catch(function (err) {
          self.showError(err.message);
        })
        .then(function () {
          self.busy = false;
          self.el.classList.remove('is-loading');
        });
    },

    showError: function (message) {
      var box = $('[data-r2g-drawer-error]', this.el);
      if (!box) {
        box = doc.createElement('div');
        box.className = 'r2g-drawer__error';
        box.setAttribute('data-r2g-drawer-error', '');
        var header = $('.r2g-drawer__header', this.el);
        if (header && header.parentNode) { header.parentNode.insertBefore(box, header.nextSibling); }
      }
      box.textContent = message || 'Something went wrong. Please try again.';
      box.style.display = 'block';
      setTimeout(function () { box.style.display = 'none'; }, 5000);
    }
  };

  /* ------------------------------------------------------------------ */
  /* Shop Pay installments: "Pay in 4 interest-free installments of $X    */
  /* with shop (?)" built from <shopify-payment-terms shopify-meta="...">  */
  /* ------------------------------------------------------------------ */
  var Installments = {
    INFO_ICON: '<svg viewBox="0 0 16 16" width="16" height="16" aria-hidden="true" focusable="false">' +
      '<circle cx="8" cy="8" r="7" fill="none" stroke="currentColor" stroke-width="1.4"/>' +
      '<path d="M6.1 6.4a1.95 1.95 0 1 1 2.9 1.7c-.7.4-1 .8-1 1.5" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/>' +
      '<circle cx="8" cy="11.9" r=".95" fill="currentColor"/></svg>',

    info: function (terms) {
      var meta;
      try { meta = JSON.parse(terms.getAttribute('shopify-meta') || 'null'); } catch (e) { meta = null; }
      if (!meta) { return null; }
      if (meta.type === 'product' && meta.variants && meta.variants.length) {
        var form = terms.closest('form');
        var input = form && form.querySelector('input[name="id"]');
        var vid = (input && input.value) || terms.getAttribute('variant-id');
        return meta.variants.filter(function (v) { return String(v.id) === String(vid); })[0] || null;
      }
      if (meta.type === 'cart') { return meta; }
      return null;
    },

    logo: function (terms, target) {
      // Reuse Shopify's own Shop logo from the native element when it is reachable, else a text wordmark.
      var root = terms.shadowRoot;
      var svg = root && (root.querySelector('[class*="logo"] svg') || root.querySelector('svg'));
      if (svg && !svg.querySelector('use')) {
        var clone = svg.cloneNode(true);
        clone.removeAttribute('class');
        clone.setAttribute('aria-hidden', 'true');
        target.innerHTML = '';
        target.appendChild(clone);
        target.setAttribute('data-r2g-shop-logo', 'svg');
        return;
      }
      if (target.getAttribute('data-r2g-shop-logo') !== 'text') {
        target.innerHTML = '<span class="r2g-shop-wordmark">shop</span>';
        target.setAttribute('data-r2g-shop-logo', 'text');
      }
    },

    render: function (wrapper) {
      var terms = wrapper.querySelector('shopify-payment-terms');
      var custom = wrapper.querySelector('[data-r2g-installments-custom]');
      var info = terms ? this.info(terms) : null;
      var ok = info && info.eligible && Number(info.number_of_payment_terms) === 4 && info.price_per_term;
      if (!ok) {
        if (custom) { custom.parentNode.removeChild(custom); }
        wrapper.classList.remove('r2g-installments--custom');
        return;
      }
      if (!custom) {
        custom = doc.createElement('p');
        custom.className = 'r2g-installments__line';
        custom.setAttribute('data-r2g-installments-custom', '');
        custom.appendChild(doc.createTextNode('Pay in 4 interest-free installments of '));
        var amount = doc.createElement('strong');
        amount.setAttribute('data-r2g-installments-amount', '');
        custom.appendChild(amount);
        custom.appendChild(doc.createTextNode(' with '));
        var logo = doc.createElement('span');
        logo.className = 'r2g-installments__logo';
        logo.setAttribute('aria-label', 'Shop Pay');
        custom.appendChild(logo);
        var btn = doc.createElement('button');
        btn.type = 'button';
        btn.className = 'r2g-installments__info';
        btn.setAttribute('aria-label', 'Learn more about Shop Pay installments');
        btn.setAttribute('data-r2g-installments-info', '');
        btn.innerHTML = this.INFO_ICON;
        custom.appendChild(btn);
        wrapper.appendChild(custom);
      }
      custom.querySelector('[data-r2g-installments-amount]').textContent = info.price_per_term;
      this.logo(terms, custom.querySelector('.r2g-installments__logo'));
      wrapper.classList.add('r2g-installments--custom');
    },

    renderAll: function (ctx) {
      var self = this;
      $$('.r2g-installments', ctx).forEach(function (w) { self.render(w); });
    },

    learnMore: function (wrapper) {
      var terms = wrapper && wrapper.querySelector('shopify-payment-terms');
      var root = terms && (terms.shadowRoot || terms);
      var btn = root && (root.querySelector('button[class*="learn"], [class*="learn-more"]') || root.querySelector('button'));
      if (btn) { btn.click(); return; }
      var note = wrapper.querySelector('[data-r2g-installments-note]');
      if (!note) {
        note = doc.createElement('p');
        note.className = 'r2g-installments__note';
        note.setAttribute('data-r2g-installments-note', '');
        note.textContent = 'Shop Pay Installments: split your order into 4 interest-free payments, one every 2 weeks. Choose Shop Pay at checkout.';
        wrapper.appendChild(note);
      } else {
        note.parentNode.removeChild(note);
      }
    },

    init: function () {
      var self = this;
      this.renderAll();
      // the native element upgrades after its deferred script loads; re-run to pick up its Shop logo
      if (window.customElements && customElements.whenDefined) {
        customElements.whenDefined('shopify-payment-terms').then(function () {
          setTimeout(function () { self.renderAll(); }, 60);
        });
      }
      doc.addEventListener('click', function (e) {
        var btn = e.target.closest('[data-r2g-installments-info]');
        if (!btn) { return; }
        e.preventDefault();
        self.learnMore(btn.closest('.r2g-installments'));
      });
      // product pages: Empire fires a jQuery window event on every variant switch
      if (window.jQuery) {
        window.jQuery(window).on('product-variant-switch.r2g', function (event, data) {
          if (!data || !data.variant) { return; }
          $$('[data-r2g-installment-variant]').forEach(function (input) {
            if (String(input.value) !== String(data.variant.id)) {
              input.value = data.variant.id;
              // the native element listens for change events bubbling through its form
              input.dispatchEvent(new Event('change', { bubbles: true }));
            }
            var wrapper = input.closest('.r2g-installments');
            if (wrapper) { self.render(wrapper); }
          });
        });
      }
    }
  };

  /* ------------------------------------------------------------------ */
  /* Cart page: after Empire updates a quantity (it fires cartcount:update */
  /* on the header) re-render the installments line and free-shipping bar */
  /* ------------------------------------------------------------------ */
  function initCartPageSync() {
    var cartSection = $('[data-section-type="static-cart"]');
    if (!cartSection || !window.jQuery) { return; }
    var pending = null;
    window.jQuery('.site-header-wrapper').on('cartcount:update.r2g', function () {
      clearTimeout(pending);
      pending = setTimeout(function () {
        fetchSection('static-cart', '/cart').then(function (dom) {
          ['[data-r2g-cart-installments]', '[data-r2g-cart-shipbar]'].forEach(function (sel) {
            var fresh = $(sel, dom);
            var current = $(sel);
            if (fresh && current) { current.innerHTML = fresh.innerHTML; }
          });
          Installments.renderAll();
        }).catch(function () { /* non-critical */ });
      }, 150);
    });
  }

  function init() {
    if (Drawer.init()) { window.R2GCartDrawer = Drawer; }
    Installments.init();
    initCartPageSync();
  }

  if (doc.readyState === 'loading') {
    doc.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
